import {
  Button,
  Checkbox,
  CloseIcon,
  DatePicker,
  Drawer,
  InputDebounce,
  Radio,
  Select,
  Text,
} from '@mercai/clever'
import { useTranslation } from 'react-i18next'

import styles from './drawer-filter.module.css'

import {
  SupplierPaymentPriorityEnum,
  SupplyOrderShipmentStatusEnum,
  SupplyOrderStatusEnum,
} from '@/types'
import { DrawerFilterProps } from './drawer-filter.type'

export const SupplierOrderDrawerFilter = ({
  isOpen,
  onHandleOpen,
  filter,
  onHandleFilter,
  suppliersData,
}: DrawerFilterProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('screenSupplierOrderManagementPage')

  const onChangeBooleanOfSingleValue = (key: string, value: string) => {
    onHandleFilter({ ...filter, [key]: value })
  }

  const getBooleanOfSingleValue = (key: string, value: string): boolean => {
    return filter[key] === value
  }

  const getBooleanOfMultiValue = (key: string, value: string): boolean => {
    const values = (filter[key] as string)?.split(',') || []
    return values.includes(value)
  }

  const onChangeBooleanOfMultiValue = (key: string, value: string) => {
    let values = (filter[key] as string)?.split(',') || []
    values = values.filter((item) => !!item)

    let newValues = ''

    if (values.includes(value)) {
      newValues = values.filter((item) => item !== value).join(',')
    } else {
      newValues = [...values, value].join(',')
    }

    onHandleFilter({ ...filter, [key]: newValues })
  }

  return (
    <Drawer isOpen={isOpen} onHandleOpen={onHandleOpen}>
      <div className={styles.header}>
        <Text variant="title-large" className={styles['header-title']}>
          {t('drawerFilterTitle')}
        </Text>

        <button
          className={styles['header-button']}
          type="button"
          onClick={onHandleOpen}
        >
          <CloseIcon />
        </button>
      </div>

      <div className={styles.content}>
        <div className={styles['content-filter']}>
          <Text variant="title-base">
            {t('tableSupplierOrderDrawerFilterHasTransaction')}
          </Text>
          <Radio
            value={getBooleanOfSingleValue('hasTransaction', 'true')}
            onChange={() =>
              onChangeBooleanOfSingleValue('hasTransaction', 'true')
            }
          >
            {t('tableSupplierOrderDrawerFilterHasTransactionYes')}
          </Radio>
          <Radio
            value={getBooleanOfSingleValue('hasTransaction', 'false')}
            onChange={() =>
              onChangeBooleanOfSingleValue('hasTransaction', 'false')
            }
          >
            {t('tableSupplierOrderDrawerFilterHasTransactionNo')}
          </Radio>
        </div>

        <div className={styles['content-filter']}>
          <Text variant="title-base">
            {t('tableSupplierOrderDrawerFilterSupplier')}
          </Text>
          <Select
            value={filter.supplier_Email || ''}
            options={[
              { label: t('tableSupplierOrderDrawerFilterNoFilled'), value: '' },
              ...(suppliersData?.map((supplier) => ({
                label: supplier.name,
                value: `${supplier?.email}`,
              })) || []),
            ]}
            onChange={(newValue) =>
              onHandleFilter({ ...filter, supplier_Email: newValue })
            }
            placeholder={t('tableSupplierOrderDrawerFilterSupplierPlaceholder')}
          />
        </div>

        <div className={styles['content-filter']}>
          <Text variant="title-base">
            {t('tableSupplierOrderDrawerFilterDestinationAddress')}
          </Text>
          <InputDebounce
            value={filter.destinationAddress || ''}
            onChange={(newValue) =>
              onHandleFilter({ ...filter, destinationAddress: newValue })
            }
            placeholder={t(
              'tableSupplierOrderDrawerFilterDestinationAddressPlaceholder',
            )}
          />
        </div>

        <div className={styles['content-filter']}>
          <Text variant="title-base">
            {t('tableSupplierOrderDrawerFilterExpectedPaid')}
          </Text>
          <div className={styles['content-filter-date']}>
            <DatePicker
              value={filter.expectedPaymentDateAfter || ''}
              onChange={(newValue) =>
                onHandleFilter({
                  ...filter,
                  expectedPaymentDateAfter: newValue || '',
                })
              }
              placeholder={t(
                'tableSupplierOrderDrawerFilterExpectedPaidStartPlaceholder',
              )}
            />
            <Text>-</Text>
            <DatePicker
              value={filter.expectedPaymentDateBefore || ''}
              onChange={(newValue) =>
                onHandleFilter({
                  ...filter,
                  expectedPaymentDateBefore: newValue || '',
                })
              }
              placeholder={t(
                'tableSupplierOrderDrawerFilterExpectedPaidEndPlaceholder',
              )}
            />
          </div>
        </div>

        <div className={styles['content-filter']}>
          <Text variant="title-base">
            {t('tableSupplierOrderDrawerFilterPaymentPriority')}
          </Text>
          <Select
            value={filter?.paymentPriority || ''}
            options={[
              { label: t('tableSupplierOrderDrawerFilterNoFilled'), value: '' },
              ...Object.keys(SupplierPaymentPriorityEnum).map((key) => ({
                label: tEnums(`supply_payment_priority_${key}`),
                value: key,
              })),
            ]}
            onChange={(newValue) =>
              onHandleFilter({
                ...filter,
                paymentPriority: newValue || '',
              })
            }
            placeholder={t(
              'tableSupplierOrderDrawerFilterPaymentPriorityPlaceholder',
            )}
          />
        </div>

        <div className={styles['content-filter']}>
          <Text variant="title-base">
            {t('tableSupplierOrderDrawerFilterStatus')}
          </Text>
          <div className={styles.grid}>
            {Object.keys(SupplyOrderStatusEnum).map((key) => (
              <Checkbox
                key={key}
                value={getBooleanOfMultiValue('status', key)}
                onChange={() => onChangeBooleanOfMultiValue('status', key)}
              >
                {tEnums(`supply_order_status_${key}`)}
              </Checkbox>
            ))}
          </div>
        </div>

        <div className={styles['content-filter']}>
          <Text variant="title-base">
            {t('tableSupplierOrderDrawerFilterShipmentStatus')}
          </Text>
          <div className={styles.grid}>
            {Object.keys(SupplyOrderShipmentStatusEnum).map((key) => (
              <Checkbox
                key={key}
                value={getBooleanOfMultiValue('shipmentStatus', key)}
                onChange={() =>
                  onChangeBooleanOfMultiValue('shipmentStatus', key)
                }
              >
                {tEnums(`supply_order_shipment_status_${key}`)}
              </Checkbox>
            ))}
          </div>
        </div>
      </div>

      <div className={styles.footer}>
        <Button variant="secondary" onClick={onHandleOpen}>
          {t('drawerFilterButtonBack')}
        </Button>
      </div>
    </Drawer>
  )
}
